import {
  LessonData,
  OpenQuestionTeacherPin,
  PinTeacherSettings,
  PinType,
  SortingQuestionPinTeacherSettingsSchema,
  TeacherPin,
  getCorrectSortingQuestionPinOrder,
  openQuestionAllCorrectAnswers,
} from '@lessonup/pins-shared';
import {
  cloneDeep,
  forEach,
  set,
  first,
  orderBy,
  isNil,
  isUndefined,
  isEmpty,
  map,
  fromPairs,
  compact,
  Dictionary,
} from 'lodash';
import { Identifiable, Modifiable, Nullable, omitDeepUndefined, Ownable, UserAccessRulable } from '../../utils';
import { OpenQuestion, QuizQuestion } from '../common';
import { FractionalIndex } from '../common/FractionalIndex';
import { AppError } from '../errors/AppError';
import { AssignmentBasicInfo } from './AssignmentBasicInfo';
import { AssignmentType } from './AssignmentComplete';
import { AssignmentContent } from './AssignmentContent';
import { AssignmentSettings } from './AssignmentSettings';

/** Dit is het loading voor de teacher voor assignments waarin alle antwoorden staan en waar de queries op uitgevoerd worden. */
/** Assignment wordt enkel opgehaald waar nodig */
export interface AssignmentMeta<T extends AssignmentType = AssignmentType>
  extends UserAccessRulable,
    Ownable,
    Identifiable,
    Modifiable {
  type: T;
  lessonId: string;
  products?: string[];
  assignmentInfo: AssignmentBasicInfo;
  teacherActivityAt?: number; // activity date for the realtime games
  label: string | undefined;
  status: AssignmentSettings.Status;
  groupId: string | undefined;
  module: string | undefined;
  // TODO @davey: replace with number?
  order: FractionalIndex.OrderKey | undefined;
  legacy?: boolean;
  scheduled?: AssignmentMeta.Scheduled;
  channel?: string;
  usersWithAccess: string[]; // TODO Needed?

  teacherSettings: { [pinId: string]: PinTeacherSettings<PinType> };
  version?: 'v2';
}

export interface AssignmentAnswers {
  [pinId: string]: AnyCorrectAnswer;
}

export type AnyCorrectAnswer = OpenQuestion.CorrectAnswer | QuizQuestion.CorrectAnswer;

export namespace AssignmentMeta {
  export interface WithSettings {
    meta: AssignmentMeta;
    settings: AssignmentSettings;
  }

  export type WithModule<T extends AssignmentType = AssignmentType> = AssignmentMeta<T> & {
    groupId: NonNullable<AssignmentMeta['groupId']>;
    module: NonNullable<AssignmentMeta['module']>;
    order: NonNullable<AssignmentMeta['order']>;
  };

  export type Scheduled = {
    startDate?: number;
    closeDate?: number;
    started?: boolean;
    closed?: boolean;
    startedDate?: number;
    closedDate?: number;
    duration?: number;
  };

  export type Realtime = AssignmentMeta<'realtime'>;

  export const excludedComparisonPaths: string[] = ['createdAt', 'modifiedAt', 'summary.modifiedAt'];

  export function scheduledFromOptions(options: AssignmentContent.TimeOptions): Scheduled | undefined {
    const startDate = options.startDate ? new Date(options.startDate).getTime() : undefined;
    const closeDate = options.closeDate ? new Date(options.closeDate).getTime() : undefined;
    const duration = options.duration;
    if (!startDate && !closeDate && !duration) return undefined;
    if (startDate && startDate < Date.now()) {
      throw new AppError('invalid-params', "start time can't be in the past");
    }
    return {
      startDate,
      closeDate,
      started: startDate ? false : undefined,
      closed: closeDate ? false : undefined,
      duration: options.duration,
    };
  }

  export function createFromLesson(
    assignmentId: string,
    type: AssignmentType,
    ownerId: string,
    now: number,
    teachers: string[],
    assignmentInfo: AssignmentBasicInfo,
    lessonData: LessonData,
    options: AssignmentContent.Options
  ): AssignmentMeta {
    const { label, groupId, legacy, module, order } = options;
    const meta: AssignmentMeta = {
      _id: assignmentId,
      status: 'open',
      type,
      groupId,
      module,
      order,
      label,
      lessonId: lessonData.lesson._id,
      products: lessonData.lesson.products || [],
      ownerId,
      createdAt: now,
      modifiedAt: now,
      usersWithAccess: teachers,
      assignmentInfo,
      teacherActivityAt: now,
      scheduled: scheduledFromOptions(options),
      legacy,
      channel: lessonData.lesson.channel || undefined,
      teacherSettings: mapTeacherSettingsForPins(lessonData.pins),
      // NOTE: This should always be "v2" after the revamp of the homework with the new lesson data model.
      version: lessonData.lesson.version, // undefined for classic lessons and "v2" for V2 lessons.
    };

    return meta;
  }

  /** creates an in memory clone with the updated data */
  // would be nice if we can merge this with the update fields in the future
  export function update(meta: AssignmentMeta, updates: AssignmentMeta.UpdateData): AssignmentMeta {
    const cloned = cloneDeep(meta);
    const updateFields = AssignmentMeta.updateFields(updates);
    forEach(updateFields, (value, field) => set(cloned, field, value));

    return cloned;
  }

  export function newest(metas: AssignmentMeta[]): AssignmentMeta | undefined {
    return first(orderByDate(metas));
  }

  export function orderByDate(metas: AssignmentMeta[]): AssignmentMeta[] {
    return orderBy(metas, (meta) => meta.createdAt);
  }

  export function isRealtime(meta: AssignmentMeta): meta is AssignmentMeta.Realtime {
    return meta.type === 'realtime';
  }

  export function isGroupAssignment(meta: AssignmentMeta): boolean {
    return !!meta.groupId;
  }

  function correctAnswersByPinType(teacherSettings: TeacherPin['teacherSettings'], pinType: TeacherPin['type']) {
    switch (pinType) {
      case 'OPEN_QUESTION': {
        const settings = teacherSettings as OpenQuestionTeacherPin['teacherSettings'];
        const combined = openQuestionAllCorrectAnswers(settings);
        return combined.length > 0 ? combined : undefined;
      }
      default: {
        return (teacherSettings as any).correctAnswers;
      }
    }
  }

  export function answer(
    meta: AssignmentMeta,
    pinId: string,
    pinType: TeacherPin['type']
  ): AnyCorrectAnswer | undefined {
    const teacherSettings = teacherSettingsForPin(meta, pinId);
    // TODO: Introduce discrimated union type for teacher settings?
    if (teacherSettings) {
      return correctAnswersByPinType(teacherSettings, pinType);
    }
    return undefined;
  }

  export function hasCorrectAnswer(
    meta: AssignmentMeta,
    pinId: string,
    pinType: TeacherPin['type']
  ): boolean | undefined {
    return answer(meta, pinId, pinType) !== undefined;
  }

  export function correctAnswerForPin<T extends AnyCorrectAnswer>(
    meta: AssignmentMeta | undefined,
    pinId: string,
    pinType: TeacherPin['type']
  ): T | undefined {
    if (meta) {
      const correctAnswer = answer(meta, pinId, pinType);
      if (correctAnswer) {
        return correctAnswer as T;
      }
    }
    return undefined;
  }

  export function isChannelBased(meta: AssignmentMeta | undefined): boolean {
    return !!meta?.channel;
  }

  export function isProductBased(meta: AssignmentMeta | undefined): boolean {
    return !!meta?.products?.length;
  }

  export function teacherSettingsForPin(meta: AssignmentMeta, pinId: string): PinTeacherSettings<PinType> | undefined {
    return meta.teacherSettings && meta.teacherSettings[pinId];
  }
}

export namespace AssignmentMeta {
  type UpdateFields = Partial<
    Pick<AssignmentMeta, 'usersWithAccess' | 'teacherActivityAt' | 'ownerId' | 'module' | 'order'>
  > &
    Partial<AssignmentMeta.Scheduled> & {
      status?: AssignmentSettings.Status;
      pinAnswer?: {
        pinId: string;
        answer: AnyCorrectAnswer;
      };
      teacherSettings?: { [pinId: string]: PinTeacherSettings<PinType> };
    };

  export type UpdateData = Nullable<UpdateFields>;

  export function updateFields(data: AssignmentMeta.UpdateData): object {
    const updateFields: Dictionary<any> = {};

    if (!isNil(data.ownerId)) updateFields['ownerId'] = data.ownerId;
    if (!isNil(data.status)) updateFields['status'] = data.status;
    if (!isNil(data.usersWithAccess)) updateFields['usersWithAccess'] = data.usersWithAccess;
    if (!isNil(data.teacherActivityAt)) updateFields['teacherActivityAt'] = data.teacherActivityAt;
    if (!isUndefined(data.startDate)) updateFields['scheduled.startDate'] = data.startDate;
    if (!isUndefined(data.started)) updateFields['scheduled.started'] = data.started;
    if (!isUndefined(data.startedDate)) updateFields['scheduled.startedDate'] = data.startedDate;
    if (!isUndefined(data.closeDate)) updateFields['scheduled.closeDate'] = data.closeDate;
    if (!isUndefined(data.closed)) updateFields['scheduled.closed'] = data.closed;
    if (!isUndefined(data.closedDate)) updateFields['scheduled.closedDate'] = data.closedDate;
    if (!isUndefined(data.duration)) updateFields['scheduled.duration'] = data.duration;
    if (!isNil(data.module)) updateFields['module'] = data.module;
    if (!isNil(data.order)) updateFields['order'] = data.order;

    if (!isNil(data.pinAnswer) && data.pinAnswer.pinId) {
      updateFields[`answers.${data.pinAnswer.pinId}`] = data.pinAnswer.answer;
    }

    if (!isNil(data.teacherSettings)) {
      Object.entries(data.teacherSettings).forEach(([pinId, settings]) => {
        if (settings) {
          Object.entries(settings).forEach(([settingKey, settingValue]) => {
            updateFields[`teacherSettings.${pinId}.${settingKey}`] = settingValue;
          });
        }
      });
    }

    const cleaned = omitDeepUndefined(updateFields);
    if (!isEmpty(cleaned)) {
      cleaned['modifiedAt'] = Date.now();
    }
    return cleaned;
  }
}

export function mapTeacherSettingsForPins(pins: TeacherPin[]): { [pinId: string]: PinTeacherSettings<PinType> } {
  const items = map(pins, (pin) => {
    switch (pin.type) {
      case 'SORTING_QUESTION':
        const correctOrder = getCorrectSortingQuestionPinOrder(pin);
        return [
          pin.id,
          SortingQuestionPinTeacherSettingsSchema.parse({
            correctOrder,
          }),
        ];
      default:
        return [pin.id, pin.teacherSettings];
    }
  });

  return fromPairs(compact(items));
}
